import { useState } from 'react';
import { Button, Tabs, Tooltip } from 'antd';
import { Line, Area } from '@ant-design/plots';
import {
  formatSOCData,
  formatVoltageData,
  formatUnixToLocalDate,
  formatCurrentDataBySoC,
  formatCurrentData,
} from '../utils/utils';
import { ThunderboltOutlined, BarChartOutlined, DashboardOutlined, DownloadOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const PowerChart = ({ data, t }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  const formattedPowerData = formatVoltageData(data);

  return (
    <Line
      data={formattedPowerData}
      xField="time"
      yField="value"
      height={300}
      autoFit={true}
      inset={10}
      colorField="type"
     
    />
  );
};

PowerChart.propTypes = {
  data: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

const CurrentChart = ({ data, t }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  const formattedCurrentData = formatCurrentData(data);

  return (
    <Line
      data={formattedCurrentData}
      xField="time"
      yField="value"
      seriesField="type"
      autoFit={true}
      height={300}
      shapeField="smooth"
      colorField="type"
      inset={10}
      xAxis={{
        title: {
          text: t('timeAxis'),
        },
        label: {
          formatter: (val) => val,
        },
      }}
      yAxis={{
        title: {
          text: t('currentA'),
          style: { fontWeight: 'bold' },
        },
      }}
      color={['#52c41a', '#f5222d']}
    />
  );
};

CurrentChart.propTypes = {
  data: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

const CurrentVsSOCChart = ({ data, t }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  const formattedData = formatCurrentDataBySoC(data);

  if (!formattedData || formattedData.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  // Dynamically compute ticks based on SoC range, with 10 less than minSoC (not below 0)
  const socValues = formattedData.map(d => d.SoCValue);
  const rawMinSoC = Math.min(...socValues);
  const rawMaxSoC = Math.max(...socValues);
  const minSoC = Math.max(0, Math.ceil(rawMinSoC / 5) * 5 - 10);
  const maxSoC = Math.floor(rawMaxSoC / 5) * 5;
  const ticks = [];
  for (let v = minSoC; v <= maxSoC; v += 10) {
    ticks.push(v);
  }
  return (
    <Line
    inset={10}
      data={{
        value: formattedData,
        transform: [
          {
            type: 'fold',
            fields: ['chargerSupplyCurrent', 'EVdemandCurrent'],
            key: 'type',
            value: 'value',
          },
        ],
      }}
      xField="SoCValue"
      yField="value"
      scale={{
        x: {
          type: "linear",
          tickMethod: () => ticks
        }
      }}
      
      height={300}
      axis={{
        x: {
          title: t('socValue'),
          label: {
            rotate: Math.PI / 4,
            autoHide: true,
            autoRotate: true,
          }
        },
      }}
      colorField="type"
    />
  );
};

CurrentVsSOCChart.propTypes = {
  data: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

const SOCChart = ({ data, t }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  const formattedSOCData = formatSOCData(data);

  return (
    <Area
      data={formattedSOCData}
      xField="intervalStart"
      yField="soc"
      inset={10}
      height={300}
      style={{
        fill: 'linear-gradient(-90deg, white 0%, #93e393 100%)',
      }}
      line={{
        style:{
          stroke: 'darkgreen',
          strokeWidth: 2,
        }
      }}
    />
  );
};

SOCChart.propTypes = {
  data: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

const EnergyChart = ({ data, t }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>{t('noDataAvailable')}</div>;
  }

  const parsedData = data
    .filter((d) => d.SoCValue != null)
    .sort((a, b) => a.created_at - b.created_at)
    .map((item) => ({
      timestamp: item.created_at * 1000,
      SoCValue: item.SoCValue,
      energy: item.value,
      chargerSupplyPower: item.chargerSupplyPower,
      EVdemandPower: item.EVdemandPower,
    }));

  return (
    <Area
      data={parsedData}
      xField="timestamp"
      yField="energy"
      height={300}
      autoFit={true}
      xAxis={{
        title: {
          text: t('timeAxis'),
        },
        label: {
          formatter: (val) =>
            new Date(val).toLocaleTimeString('en-US', { hour12: false }),
        },
      }}
      yAxis={{
        title: {
          text: t('energyWh'),
          style: { fontWeight: 'bold' },
        },
      }}
      tooltip={{
        fields: ['timestamp', 'energy'],
      }}
    />
  );
};

EnergyChart.propTypes = {
  data: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

const ChargingSessionCharts = ({ data ,transactionId}) => {
  const [activeTab, setActiveTab] = useState('current');
  const { t } = useTranslation('transcationModal');

  // Determine AC or DC based on SoCValue average
  const socValues = Array.isArray(data) ? data.map(d => Number(d.SoCValue) || 0) : [];
  const socAvg = socValues.length ? socValues.reduce((a, b) => a + b, 0) / socValues.length : 0;
  const isDC = socAvg > 0;
  // Usage: isDC ? "DC" : "AC"

  const renderChart = () => {
    switch (activeTab) {
      case 'power':
        return <PowerChart data={data} t={t} />;
      case 'current':
        return isDC ? <CurrentVsSOCChart data={data} t={t} /> : <CurrentChart data={data} t={t} />;
      case 'soc':
        return <SOCChart data={data} t={t} />;
      case 'energy':
        return <EnergyChart data={data} t={t} />;
      default:
        return null;
    }
  };

   const handleExportCSV = () => {
      if (!data?.length) return;
  
      const csvData = data.map((log) => ({
        Timestamp: formatUnixToLocalDate(log.created_at),
        Context: log.context,
        Measurand: log.measurand,
        'Value (kWh)': log.value,
        'Soc (%)': log.SoCValue.toFixed(0),
        'Charger Power (W)': log.chargerSupplyPower.toFixed(0),
        'Charger Voltage (V)': log.chargerSupplyVoltage.toFixed(0),
        'Charger Current (A)': log.chargerSupplyCurrent.toFixed(0),
        'EV Demand Power (W)': log.EVdemandPower.toFixed(0),
        'EV Demand Voltage (V)': log.EVdemandVoltage.toFixed(0),
        'EV Demand Current (A)': log.EVdemandCurrent.toFixed(0),
        'Charger Cable Temperature': log.chargerCableTemperature,
        'Charger Body Temperature': log.chargerBodyTemperature,
        'Charger Inlet Temperature': log.chrgerInletTemperature,
      }));
  
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `TransactionLogs_${transactionId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
  

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={
          <div className="flex justify-end mb-4">
          <Tooltip title={t('exportCSV')}>
            <Button
              type="default"
              onClick={handleExportCSV}
              icon={<DownloadOutlined />}
              className="text-right"
            />
          </Tooltip>
        </div>
        }
        items={[
          {
            key: 'current',
            label: (
              <>
                <BarChartOutlined style={{ marginRight: 6 }} />
                {t('currentComparison')}
              </>
            ),
          },
          {
            key: 'power',
            label: (
              <>
                <ThunderboltOutlined style={{ marginRight: 6 }} />
                {t('chargerVsEVPower')}
              </>
            ),
          },
          {
            key: 'soc',
            label: (
              <Tooltip title={t('socOverTimeTooltip')}>
                <span style={{ cursor: 'help' }}>
                  <DashboardOutlined style={{ marginRight: 6 }} />
                  {t('socOverTime')}
                </span>
              </Tooltip>
            ),
          },
        ]}
      />
      {renderChart()}
    </>
  );
};

ChargingSessionCharts.propTypes = {
  data: PropTypes.array.isRequired,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ChargingSessionCharts;
